import React from "react";
import { ResponsivePie } from "@nivo/pie";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useGetCategoryAnalyticsQuery } from "state/api";

const BreakdownChart = ({ isDashboard = false }) => {
  const { data, isLoading } = useGetCategoryAnalyticsQuery();
  const theme = useTheme();

  // Themed loading state consistent with the shared design system (DataTable
  // also uses CircularProgress) instead of a bare unstyled "Loading..." string.
  if (isLoading || !data) {
    return (
      <Box
        height={isDashboard ? "400px" : "100%"}
        minHeight={isDashboard ? "325px" : undefined}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Pie-slice fill colors. The `secondary` teal ramp is intentionally used for
  // the coloured slices themselves (not for text), so it is fine here.
  const colors = [
    theme.palette.secondary[500],
    theme.palette.secondary[300],
    theme.palette.secondary[600],
    theme.palette.secondary[400],
    theme.palette.secondary[200],
  ];
  const rows = data?.data ?? [];
  const formattedData = rows.map((row, i) => ({
    id: row.category,
    label: row.category,
    value: row.revenue || 0,
    color: colors[i % colors.length],
  }));
  const totalRevenue = rows.reduce((sum, row) => sum + (row.revenue || 0), 0);

  // Clean empty state — never fabricate data.
  if (formattedData.length === 0) {
    return (
      <Box
        height={isDashboard ? "400px" : "100%"}
        minHeight={isDashboard ? "325px" : undefined}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography color="text.secondary">
          No category sales data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      height={isDashboard ? "400px" : "100%"}
      width={undefined}
      minHeight={isDashboard ? "325px" : undefined}
      minWidth={isDashboard ? "325px" : undefined}
      position="relative"
    >
      <ResponsivePie
        data={formattedData}
        theme={{
          // Mode-aware text tokens so labels/legends stay legible in both
          // light and dark mode (the `secondary` ramp is not mode-reversed).
          text: {
            fill: theme.palette.text.secondary,
          },
          legends: {
            text: {
              fill: theme.palette.text.secondary,
            },
          },
          tooltip: {
            container: {
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          },
        }}
        colors={{ datum: "data.color" }}
        margin={
          isDashboard
            ? { top: 40, right: 80, bottom: 100, left: 50 }
            : { top: 40, right: 80, bottom: 80, left: 80 }
        }
        sortByValue={true}
        innerRadius={0.45}
        activeOuterRadiusOffset={8}
        borderWidth={1}
        borderColor={{
          from: "color",
          modifiers: [["darker", 0.2]],
        }}
        enableArcLinkLabels={!isDashboard}
        arcLinkLabelsTextColor={theme.palette.text.secondary}
        arcLinkLabelsThickness={2}
        arcLinkLabelsColor={{ from: "color" }}
        arcLabelsSkipAngle={10}
        arcLabelsTextColor={{
          from: "color",
          modifiers: [["darker", 2]],
        }}
        legends={[
          {
            anchor: "bottom",
            direction: "row",
            justify: false,
            translateX: isDashboard ? 20 : 0,
            translateY: isDashboard ? 50 : 56,
            itemsSpacing: 0,
            itemWidth: 85,
            itemHeight: 18,
            itemTextColor: theme.palette.text.secondary,
            itemDirection: "left-to-right",
            itemOpacity: 1,
            symbolSize: 18,
            symbolShape: "circle",
            effects: [
              {
                on: "hover",
                style: {
                  itemTextColor: theme.palette.primary.main,
                },
              },
            ],
          },
        ]}
      />
      <Box
        position="absolute"
        top="50%"
        left="50%"
        color={theme.palette.text.primary}
        textAlign="center"
        pointerEvents="none"
        sx={{
          transform: isDashboard
            ? "translate(-75%, -170%)"
            : "translate(-50%, -100%)",
        }}
      >
        <Typography variant="h6">
          {!isDashboard && "Total:"} ₹{Number(totalRevenue).toLocaleString("en-IN")}
        </Typography>
      </Box>
    </Box>
  );
};

export default BreakdownChart;
